import React from 'react';
import ProductTable from '../ProductTable';
import HSNSummary from '../HSNSummary';

const StandardTemplate = ({
    invoiceSummary,
    products,
    sellerDetails,
    buyerDetails,
    amountInWords,
    gstType,
    isInterState,
    formatCurrency,
    invoiceNumber,
    invoiceDate,
    logo,
    shippingAddress,
    isSameAddress,
    showSignature,
    transportDetails
}) => {
    return (
        <div className="bg-white p-6 pb-10 shadow-lg border-t-8 border-blue-900 rounded-sm w-[794px] min-h-[1123px] mx-auto animate-in zoom-in-95 duration-200">
            {/* Invoice Header */}
            <div className="flex flex-row justify-between items-start mb-3 border-b border-slate-200 pb-4 relative">
                {/* App Brand - Small and in corner as requested, in original blue */}
                <div>
                    <h1 className="text-3xl font-bold text-blue-900 uppercase tracking-tight mb-2">
                        {gstType === 'Regular GST' ? 'Tax Invoice' :
                            gstType === 'Composition GST' ? 'Bill of Supply' : 'Invoice'}
                    </h1>

                    {/* Business Logo - Shown Prominently if uploaded */}
                    {logo && (
                        <div className="mt-2">
                            <img
                                src={logo}
                                alt="Business Logo"
                                className="object-contain"
                                style={{ height: '80px', maxWidth: '150px', width: 'auto' }}
                            />
                        </div>
                    )}
                </div>
                <div className="text-right">
                    {/* Clean ERP Logo - Right side above Tax Invoice, pushed down slightly */}
                    <div className="flex justify-end mb-1 mt-2">
                        <img src="/logo.png" alt="Clean ERP" className="h-4 w-auto object-contain" />
                    </div>
                    <div className="text-slate-500 text-sm mb-1">Invoice Date</div>
                    <div className="text-slate-900 font-semibold">
                        {new Date(invoiceDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                    <div className="text-slate-500 text-sm mt-2 mb-1">Invoice No.</div>
                    <div className="text-blue-900 font-bold text-lg">{invoiceNumber || 'INV-001'}</div>
                </div>
            </div>

            {/* Seller & Buyer Details + Addresses (New Layout) */}
            <div className="mb-8">
                {/* Top Row: Seller and Buyer */}
                <div className="grid grid-cols-2 gap-8 mb-6">
                    {/* Seller Info */}
                    <div>
                        <div className="mb-2"><span className="text-xs font-bold text-slate-500 uppercase tracking-wider block border-b border-slate-200 pb-1 mb-2">Seller Info</span></div>
                        <div className="text-slate-900 font-bold text-lg">{sellerDetails.name}</div>
                        {sellerDetails.address && <div className="text-sm text-slate-600 mb-1 whitespace-pre-wrap">{sellerDetails.address}</div>}
                        {sellerDetails.state && <div className="text-sm text-slate-600">State: {sellerDetails.state}</div>}
                        {sellerDetails.gstNumber && <div className="text-sm text-slate-600 mt-1">GSTIN: {sellerDetails.gstNumber}</div>}
                    </div>

                    {/* Buyer Info */}
                    <div>
                        <div className="mb-2"><span className="text-xs font-bold text-slate-500 uppercase tracking-wider block border-b border-slate-200 pb-1 mb-2">Buyer Info</span></div>
                        <div className="text-slate-900 font-bold text-lg">{buyerDetails.name}</div>
                        {buyerDetails.address && <div className="text-sm text-slate-600 mb-1 whitespace-pre-wrap">{buyerDetails.address}</div>}
                        {buyerDetails.state && <div className="text-sm text-slate-600">State: {buyerDetails.state}</div>}
                        {buyerDetails.gstNumber && buyerDetails.gstNumber !== 'NOT APPLICABLE' && <div className="text-sm text-slate-600 mt-1">GSTIN: {buyerDetails.gstNumber}</div>}
                    </div>
                </div>

                {/* Bottom Row: Billing Address (Left) and Shipping Address (Right) */}
                <div className="grid grid-cols-2 gap-8">
                    {/* Billing Address - Left */}
                    <div>
                        <div className="mb-2"><span className="text-xs font-bold text-slate-500 uppercase tracking-wider block border-b border-slate-200 pb-1 mb-2">Billing Address</span></div>
                        <div className="text-sm text-slate-700 whitespace-pre-wrap">{buyerDetails.address}</div>
                    </div>

                    {/* Shipping Address - Right */}
                    <div>
                        <div className="mb-2"><span className="text-xs font-bold text-slate-500 uppercase tracking-wider block border-b border-slate-200 pb-1 mb-2">Shipping Address</span></div>
                        <div className="text-sm text-slate-700 whitespace-pre-wrap">{isSameAddress ? buyerDetails.address : shippingAddress}</div>
                    </div>
                </div>
            </div>

            {/* Transport Details Section - Matches other blocks */}
            {transportDetails && (transportDetails.transporterName || transportDetails.vehicleNo || transportDetails.ewayBillNo) && (
                <div className="mb-6 border-t border-slate-200 pt-4">
                    <div className="mb-2"><span className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">Transport Details</span></div>
                    <div className="grid grid-cols-2 gap-8 text-sm">
                        <div className="space-y-1">
                            {transportDetails.transporterName && (
                                <div className="text-slate-900"><span className="text-slate-500 mr-2">Transporter:</span><span className="font-semibold">{transportDetails.transporterName}</span></div>
                            )}
                            {transportDetails.transporterGst && (
                                <div className="text-slate-900"><span className="text-slate-500 mr-2">GSTIN:</span><span className="font-semibold">{transportDetails.transporterGst}</span></div>
                            )}
                        </div>
                        <div className="space-y-1">
                            {transportDetails.vehicleNo && (
                                <div className="text-slate-900"><span className="text-slate-500 mr-2">Vehicle No:</span><span className="font-semibold">{transportDetails.vehicleNo}</span></div>
                            )}
                            {transportDetails.ewayBillNo && (
                                <div className="text-slate-900"><span className="text-slate-500 mr-2">E-Way Bill:</span><span className="font-semibold">{transportDetails.ewayBillNo}</span></div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ProductTable
                products={products}
                isInterState={isInterState}
                gstType={gstType}
                isPreview={true}
                stateTaxRate={9}
            />

            {gstType === 'Regular GST' && (
                <HSNSummary
                    hsnSummary={invoiceSummary.hsnSummary || []}
                    isBillOfSupply={gstType !== 'Regular GST'}
                    isInterState={isInterState}
                />
            )}

            {/* Footer Totals & Words */}
            <div className="flex flex-row justify-between items-start gap-8 mt-4 pt-6 border-t border-slate-200">
                <div className="w-1/2">
                    <div className="mb-2"><span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Amount in Words</span></div>
                    <div className="text-slate-800 font-medium text-sm border-l-4 border-blue-900 pl-4 py-2 bg-slate-50">{amountInWords}</div>
                </div>
                <div className="w-5/12 bg-slate-50 p-4 rounded border border-slate-200">
                    {/* Simplified Total Display for Preview */}
                    <div className="flex justify-between items-center text-slate-600 text-sm">
                        <span>{gstType !== 'Regular GST' ? 'Sub Total' : 'Taxable Amount'}</span>
                        <span className="font-mono font-medium">{formatCurrency(invoiceSummary.totalTaxable)}</span>
                    </div>
                    {gstType === 'Regular GST' && (
                        <>
                            {isInterState ? (
                                <div className="flex justify-between items-center text-slate-600 text-sm">
                                    <span>IGST Total</span>
                                    <span className="font-mono font-medium">{formatCurrency(invoiceSummary.igst || 0)}</span>
                                </div>
                            ) : (
                                <>
                                    <div className="flex justify-between items-center text-slate-600 text-sm">
                                        <span>CGST Total</span>
                                        <span className="font-mono font-medium">{formatCurrency(invoiceSummary.cgst || 0)}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-slate-600 text-sm">
                                        <span>SGST Total</span>
                                        <span className="font-mono font-medium">{formatCurrency(invoiceSummary.sgst || 0)}</span>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                    <div className="flex justify-between items-center text-xl font-bold text-blue-900 pt-2 mt-2 border-t-2 border-slate-200">
                        <span>Grand Total</span>
                        <span>{formatCurrency(invoiceSummary.grandTotal)}</span>
                    </div>
                </div>
            </div>

            {showSignature && (
                <div className="mt-2 pt-4 border-t border-slate-200 flex justify-end animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="text-center">
                        <div className="mb-8">
                            <span className="text-lg font-signature text-slate-800">{sellerDetails.name || 'Authorized Signatory'}</span>
                        </div>
                        <p className="text-sm font-medium text-slate-600 border-t border-slate-300 pt-2 px-4">Authorized Signatory</p>
                    </div>
                </div>
            )}

            <div className="mt-8 text-center text-slate-500 text-xs font-medium">
                Thank you for your business
            </div>
        </div>
    );
};

export default StandardTemplate;
